import { useState, useRef, useCallback } from "react";

export interface QuestionOption {
  label: string;
  text: string;
  correct: boolean;
  feedback: string;
}

export interface Question {
  skill: string;
  riven: string;
  prompt?: string;
  options: QuestionOption[];
}

interface QuestionCardProps {
  question: Question;
  submitted: boolean;
  onChoose: (option: QuestionOption) => void;
  playTone: (kind: string) => void;
}

export default function QuestionCard({ question, submitted, onChoose, playTone }: QuestionCardProps) {
  const [selectedOption, setSelectedOption] = useState<QuestionOption | null>(null);
  const lastHover = useRef(0);

  const handleHover = useCallback(() => {
    if (selectedOption) return;
    const t = Date.now();
    if (t - lastHover.current > 120) {
      lastHover.current = t;
      playTone("hover");
    }
  }, [playTone, selectedOption]);

  const handleSelect = (opt: QuestionOption) => {
    if (submitted) return;
    setSelectedOption(opt);
    playTone("tap");
  };

  const handleSubmit = () => {
    if (!selectedOption || submitted) return;
    onChoose(selectedOption);
  };

  return (
    <div className="assess-content">
      <div className="assess-chat">
        <div className="assess-speaker">
          <div className="assess-speaker-info">
            <div className="assess-avatar" aria-hidden="true" />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{question.skill}</div>
            </div>
          </div>
        </div>
        <p className="assess-line">{question.riven}</p>
        {question.prompt && <p className="assess-prompt">{question.prompt}</p>}
      </div>

      <div className="assess-choices">
        <div className="assess-choice-grid">
          {question.options.map((opt, i) => (
            <button
              key={opt.label}
              className={`assess-btn${selectedOption === opt ? " selected" : ""}`}
              disabled={submitted}
              onClick={() => handleSelect(opt)}
              onMouseEnter={handleHover}
              style={{
                opacity: 0,
                transform: "translateY(10px)",
                animation: `assess-completionEnter 0.3s ease ${80 * i}ms forwards`,
              }}
            >
              <span className="assess-btn-label">
                <span className="assess-chip">{opt.label}</span>
              </span>
              <span className="assess-btn-text">{opt.text}</span>
            </button>
          ))}
        </div>
        <div className="assess-submit-row">
          <button
            className="assess-primary-btn assess-submit-btn"
            disabled={!selectedOption || submitted}
            onClick={handleSubmit}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}
